import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Building2, CheckCircle, AlertCircle, Lock, User, Mail, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await fetch(`${API_URL}/api/invite/${token}`);
        if (res.ok) {
          const data = await res.json();
          setInviteData(data);
        } else {
          const err = await res.json();
          setError(err.detail || 'Invito non valido');
        }
      } catch (error) {
        setError('Errore di connessione');
      }
      setLoading(false);
    };
    
    fetchInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }
    
    if (password.length < 8) {
      toast.error('La password deve essere almeno 8 caratteri');
      return;
    }
    
    if (!privacyConsent) {
      toast.error('Devi accettare l\'informativa privacy');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/invite/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          privacy_consent: privacyConsent
        })
      });
      
      if (res.ok) {
        setSuccess(true);
        toast.success('Account creato con successo!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore registrazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifica invito...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invito non valido</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/login">
              <Button variant="outline">Vai al Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Account Creato!</h2>
            <p className="text-muted-foreground mb-6">
              Il tuo account è stato creato con successo. 
              Ora puoi accedere con la tua email e password.
            </p>
            <Link to="/login">
              <Button className="w-full">Accedi</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-4 bg-slate-100 rounded-full w-fit">
            <Building2 className="h-8 w-8 text-slate-700" />
          </div>
          <CardTitle>Completa la Registrazione</CardTitle>
          <CardDescription>
            Sei stato invitato a {inviteData?.condominium_name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Invite Info */}
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{inviteData?.condominium_name}</span>
              </div>
              {inviteData?.condominium_address && (
                <p className="text-xs text-muted-foreground pl-6">
                  {inviteData.condominium_address}
                </p>
              )}
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{inviteData?.person_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{inviteData?.email}</span>
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Almeno 8 caratteri"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Conferma Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ripeti la password"
                  required
                />
              </div>
            </div>

            {/* Privacy Consent */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  checked={privacyConsent}
                  onCheckedChange={(c) => setPrivacyConsent(!!c)}
                />
                <Label htmlFor="privacy" className="text-sm cursor-pointer leading-relaxed">
                  <Shield className="h-4 w-4 inline mr-1 text-muted-foreground" />
                  Ho letto e accetto l'informativa sulla privacy e acconsento al 
                  trattamento dei miei dati personali per la gestione condominiale
                </Label>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Creazione account...' : 'Completa Registrazione'}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Hai già un account?{' '}
              <Link to="/login" className="text-slate-900 hover:underline">
                Accedi qui
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
